import React from 'react';
import { Image, RefreshCw, UploadCloud, Trash2, XCircle } from 'lucide-react';
import { SPLASH_ACCEPT_ATTR, getSplashMediaKind } from '../../../utils/splashMedia';
import { text } from '../constants/configurationText';

const SplashSection = ({
  theme,
  styles,
  t = text.en,
  selectedCompany,
  splashInfo,
  loadingSplash,
  uploadingSplash,
  splashPreviewUrl,
  splashUrlInput,
  setSplashUrlInput,
  splashActive,
  iconPreviewUrl,
  iconUrlInput,
  setIconUrlInput,
  setIconPreviewUrl,
  iconFile,
  splashFile,
  splashSectionRef,
  onSplashFileChange,
  onClearSplash,
  onIconFileChange,
  onClearIcon,
  iconRemoved,
  onRemoveIcon,
  onSave,
  onDeactivate,
  onToggleActive,
}) => {
  const splashMediaKind = getSplashMediaKind(splashFile || splashPreviewUrl);

  return (
    <div ref={splashSectionRef} style={{ ...styles.splashCard, scrollMarginTop: '24px' }}>
      {/* Header */}
      <div style={styles.splashHeader}>
        <div style={styles.subCardTitleRow}>
          <span style={styles.subCardIcon}>
            <Image size={18} />
          </span>
          <div>
            <h4 style={styles.subCardTitle}>{t.splashTitle}</h4>
            <p style={styles.subCardSubtitle}>{t.splashFor(selectedCompany)}</p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{
            fontSize: "12.5px",
            fontWeight: "700",
            color: splashActive ? "#10b981" : "#ef4444",
          }}>
            {splashActive ? t.active : t.inactive}
          </span>
          <div style={styles.toggle(splashActive)} onClick={onToggleActive}>
            <div style={styles.toggleKnob(splashActive)} />
          </div>
        </div>
      </div>

      {/* Splash Preview */}
      {loadingSplash ? (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          padding: "24px",
          fontSize: "13.5px",
          color: styles.colors.muted,
        }}>
          <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
          {t.loadingSplash}
        </div>
      ) : (
        <div style={{ marginBottom: "18px" }}>
          {splashPreviewUrl ? (
            splashMediaKind === 'video' ? (
              <video
                key={splashPreviewUrl}
                src={splashPreviewUrl}
                style={styles.splashPreview}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
              />
            ) : (
              <img src={splashPreviewUrl} alt={t.splashPreviewAlt} style={styles.splashPreview} />
            )
          ) : (
            <div style={{
              ...styles.splashPreview,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: styles.colors.faint,
              gap: "8px",
              fontSize: "13px",
            }}>
              <Image size={28} />
              {t.noSplash}
            </div>
          )}
        </div>
      )}

      {/* GIF / Video Inputs */}
      <div style={styles.splashInputRow}>
        <div>
          <label style={styles.fieldLabel}>{t.uploadSplash}</label>
          <input
            type="file"
            className="cfg-input"
            accept={SPLASH_ACCEPT_ATTR}
            onChange={onSplashFileChange}
            disabled={uploadingSplash}
            style={styles.splashInput}
          />
        </div>
        <div>
          <label style={styles.fieldLabel}>{t.splashUrl}</label>
          <input
            type="text"
            className="cfg-input"
            value={splashUrlInput}
            onChange={(e) => setSplashUrlInput(e.target.value)}
            placeholder="https://example.com/splash.mp4"
            style={styles.splashInput}
            disabled={uploadingSplash}
          />
        </div>
      </div>

      <p style={styles.hint}>
        {t.splashHint}
      </p>

      {/* Icon Section */}
      <div style={styles.iconContainer}>
        <div style={styles.iconHeaderRow}>
          <div>
            <h5 style={styles.iconTitle(theme)}>{t.iconTitle}</h5>
            <p style={styles.iconSubtitle(theme)}>
              {t.iconFor(selectedCompany)}
            </p>
          </div>
        </div>

        <div style={{ marginBottom: "18px" }}>
          {iconPreviewUrl ? (
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <img src={iconPreviewUrl} alt={t.iconPreviewAlt} style={styles.iconPreview} />
              <button
                type="button"
                className="cfg-btn-secondary"
                style={styles.btnSecondary(uploadingSplash)}
                onClick={onRemoveIcon}
                disabled={uploadingSplash}
              >
                <Trash2 size={16} />
                {t.removeIcon}
              </button>
            </div>
          ) : (
            <div style={styles.iconPlaceholder(theme)}>
              <Image size={20} />
              {iconRemoved ? t.iconWillRemove : t.noIcon}
            </div>
          )}
        </div>

        <div style={styles.iconInputRow}>
          <div>
            <label style={styles.iconLabel(theme)}>{t.uploadIcon}</label>
            <input
              type="file"
              className="cfg-input"
              accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp,image/x-icon,image/vnd.microsoft.icon,.ico"
              onChange={onIconFileChange}
              disabled={uploadingSplash}
              style={styles.splashInput}
            />
          </div>
          <div>
            <label style={styles.iconLabel(theme)}>{t.iconUrl}</label>
            <input
              type="text"
              className="cfg-input"
              value={iconUrlInput}
              onChange={(e) => {
                const value = e.target.value;
                setIconUrlInput(value);
                if (!iconFile) {
                  setIconPreviewUrl(value.trim() ? value.trim() : null);
                }
              }}
              placeholder="https://example.com/icon.png"
              style={styles.splashInput}
              disabled={uploadingSplash}
            />
          </div>
        </div>

        <div style={styles.iconHint(theme)}>
          {t.iconHint}
        </div>
      </div>

      {/* Actions */}
      <div style={styles.splashActions}>
        <button
          className="cfg-btn-primary"
          style={styles.btnPrimary(uploadingSplash || loadingSplash)}
          onClick={onSave}
          disabled={uploadingSplash || loadingSplash}
        >
          {uploadingSplash ? (
            <>
              <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
              {t.saving}
            </>
          ) : (
            <>
              <UploadCloud size={16} />
              {t.saveSplash}
            </>
          )}
        </button>

        {(splashFile || splashUrlInput || iconFile || iconUrlInput || iconRemoved) && (
          <button
            className="cfg-btn-secondary"
            style={styles.btnSecondary(uploadingSplash)}
            onClick={() => { onClearSplash(); onClearIcon(); }}
            disabled={uploadingSplash}
          >
            <Trash2 size={16} />
            {t.clearSelection}
          </button>
        )}

        {splashInfo && splashInfo.isActive && (
          <button
            style={{ ...styles.dangerSoftButton, opacity: uploadingSplash || loadingSplash ? 0.55 : 1 }}
            onClick={onDeactivate}
            disabled={uploadingSplash || loadingSplash}
          >
            <XCircle size={16} />
            {t.deactivateSplash}
          </button>
        )}
      </div>
    </div>
  );
};

export default SplashSection;